import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import { ValueOfAssets as ValueOfAssetsType, Maybe } from '@/graphql/__generated__/van-credits';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface ValueOfAssetsProps {
	data: Maybe<ValueOfAssetsType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	homeValue = 'home_value',
	landValue = 'land_value',
	carsValue = 'cars_value',
	equipmentValue = 'equipment_value',
	equipmentDetail = 'equipment_detail',
	savings = 'savings'
}

const ValueOfAssets: React.FC<ValueOfAssetsProps> = ({ data, title, isEditing, inputErrors }) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');
	const { valueOfAssets, updateValueOfAssets } = useUpdateVanQuestionnaireStore();
	const [formData, setFormData] = useState(valueOfAssets as ValueOfAssetsType);

	useEffect(() => {
		setFormData(valueOfAssets);
	}, [valueOfAssets]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateValueOfAssets({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'homeValue'}
						value={formData['homeValue'] || data?.homeValue || ''}
						onChange={(newValue) => handleChange('homeValue', newValue)}
						error={inputErrors?.[inputErrorMapping.homeValue] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'landValue'}
						value={formData['landValue'] || data?.landValue || ''}
						onChange={(newValue) => handleChange('landValue', newValue)}
						error={inputErrors?.[inputErrorMapping.landValue] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'carsValue'}
						value={formData['carsValue'] || data?.carsValue || ''}
						onChange={(newValue) => handleChange('carsValue', newValue)}
						error={inputErrors?.[inputErrorMapping.carsValue] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'equipmentValue'}
						value={formData['equipmentValue'] || data?.equipmentValue || ''}
						onChange={(newValue) => handleChange('equipmentValue', newValue)}
						error={inputErrors?.[inputErrorMapping.equipmentValue] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'equipmentDetail'}
						value={
							formData['equipmentDetail'] !== null
								? formData['equipmentDetail']
								: data?.equipmentDetail || ''
						}
						onChange={(newValue) => handleChange('equipmentDetail', newValue)}
						error={inputErrors?.[inputErrorMapping.equipmentDetail] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'savings'}
						value={formData['savings'] || data?.savings || ''}
						onChange={(newValue) => handleChange('savings', newValue)}
						error={inputErrors?.[inputErrorMapping.savings] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			<div className="flex flex-row w-full">
				<div className="flex flex-row flex-wrap w-full">
					{isEditing ? (
						editableFields()
					) : (
						<>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput title={'homeValue'} value={formatValue(data?.homeValue, t)} />
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput title={'landValue'} value={formatValue(data?.landValue, t)} />
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput title={'carsValue'} value={formatValue(data?.carsValue, t)} />
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'equipmentValue'}
									value={formatValue(data?.equipmentValue, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'equipmentDetail'}
									value={formatValue(data?.equipmentDetail, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput title={'savings'} value={formatValue(data?.savings, t)} />
							</div>
							<div className="w-1/2 px-3 py-1"></div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'totalValueOfAssets'}
									value={formatValue(data?.totalValueOfAssets, t)}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default ValueOfAssets;
